import { useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Column } from 'components/v2/Column'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Row } from 'components/v2/Row'
import { IVehicleDetailsFormValues } from 'features/Vehicles/types'
import { STATES } from 'utils/states'
import { Label } from 'components/v2/Label'
import { COUNTRY_CODES } from 'utils/countries'
import { SelectState } from 'features/Cities/components/SelectState'
import { ICompany } from 'features/Companies/types'
import { MultiCompanyAutocomplete } from 'features/Companies/components/MultiCompanyAutocomplete'
import { GTCompaniesV2Autocomplete } from 'gql/types.generated'
import { canSelectCompany } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'

const messages = defineMessages({
  namePlaceholder: {
    id: 'vehicles.form.name.placeholder',
    defaultMessage: 'Enter the vehicle name'
  },
  vinPlaceholder: {
    id: 'vehicles.form.vin.placeholder',
    defaultMessage: 'Serial number'
  },
  cityNamePlaceholder: {
    id: 'vehicles.form.cityName.placeholder',
    defaultMessage: 'Enter the city name'
  },
  companyPlaceholder: {
    id: 'vehicles.form.company.placeholder',
    defaultMessage: 'Select a company'
  }
})

interface IVehicleDetailsFormProps {
  initialCompanies?: {
    id: string
    name: string
  }[]
}

export const VehicleDetailsForm = ({ initialCompanies }: IVehicleDetailsFormProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const { values, handleChange, setFieldValue, handleBlur, touched, errors } =
    useFormikContext<IVehicleDetailsFormValues>()

  const handleStateChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
    const stateName = value && STATES.find((state) => state.code === value)?.name

    if (stateName) {
      setFieldValue('state', {
        code: value,
        name: stateName
      })
    } else {
      setFieldValue('state', null)
    }
  }

  const handleNameChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('name', value.toUpperCase())
  }

  return (
    <>
      <Heading.H4>
        <FormattedMessage id="vehicles.form.vehicleDetails.title" defaultMessage="Vehicle Details" />
      </Heading.H4>
      {canSelectCompany(login.accessToken) && (
        <Row noMargin>
          <Column width="50%">
            <Label>
              <FormattedMessage id="vehicles.form.company.label" defaultMessage="Company" />
            </Label>
            <MultiCompanyAutocomplete
              id="company"
              placeholder={intl.formatMessage(messages.companyPlaceholder)}
              values={values.companies as GTCompaniesV2Autocomplete[]}
              onChange={(values: ICompany[]): void => {
                setFieldValue('companies', values)
              }}
              onBlur={handleBlur}
              touched={touched.companies}
              errors={errors.companies}
              readOnlyValues={initialCompanies || []}
            />
          </Column>
        </Row>
      )}
      <Row noMargin>
        <Column width="50%">
          <Input
            id="name"
            value={values.name}
            onChange={handleNameChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.namePlaceholder)}
            label={<FormattedMessage id="vehicles.form.name.label" defaultMessage="Vehicle name" />}
            touched={touched.name}
            errors={errors.name}
          />
        </Column>
        <Column width="50%">
          <Input
            id="vin"
            value={values.vin}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.vinPlaceholder)}
            label={<FormattedMessage id="vehicles.form.vin.label" defaultMessage="Vin" />}
            touched={touched.vin}
            errors={errors.vin}
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column width="50%">
          <Input
            id="cityName"
            value={values.cityName || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.cityNamePlaceholder)}
            label={<FormattedMessage id="vehicles.form.cityName.label" defaultMessage="City name" />}
            touched={touched.cityName}
            errors={errors.cityName}
            optional
          />
        </Column>
        <Column width="50%">
          <Label optional>
            <FormattedMessage id="dictionary.state" defaultMessage="State" />
          </Label>
          <SelectState
            id="stateCode"
            value={values.state?.code || ''}
            onChange={handleStateChange}
            onBlur={handleBlur}
            country={COUNTRY_CODES.MEXICO}
          />
        </Column>
      </Row>
    </>
  )
}
